import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { completeAuth } = useAuth();
  const state = location.state || {};
  const email = state.email;
  const purpose = state.purpose; // 'signup' | 'login'

  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!email || !purpose) navigate("/auth/login", { replace: true });
  }, [email, purpose, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // Start with a 60s cooldown so the user can't immediately resend.
  useEffect(() => { setCooldown(60); inputsRef.current[0]?.focus(); }, []);

  const onChange = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputsRef.current[i + 1]?.focus();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setDigits(text.split(""));
      inputsRef.current[5]?.focus();
    }
  };

  const submit = useCallback(async (codeStr) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", {
        email, code: codeStr, purpose,
      });
      completeAuth(data.token, data.user);
      toast.success(purpose === "signup" ? "Compte vérifié — bienvenue !" : "Connexion réussie !");
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
      setDigits(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  }, [email, purpose, completeAuth, navigate]);

  const onSubmit = (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== 6) return;
    submit(code);
  };

  // Auto-submit when 6 digits are entered
  useEffect(() => {
    const code = digits.join("");
    if (code.length === 6 && !loading) submit(code);
  }, [digits, loading, submit]);

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await api.post("/auth/resend-otp", { email, purpose });
      toast.success("Nouveau code envoyé. Vérifiez votre boîte mail.");
      setCooldown(60);
      setDigits(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Réessayez dans un instant.");
    }
  };

  if (!email || !purpose) return null;

  return (
    <div className="min-h-screen bg-af grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-af-hero border-r border-af">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={52} />
          <span className="font-display font-extrabold text-2xl">AfriMeet</span>
        </Link>
        <div>
          <div className="h-12 w-12 rounded-md bg-[#F5EFD8] border border-af flex items-center justify-center mb-5">
            <ShieldCheck className="h-6 w-6 text-terracotta" />
          </div>
          <h2 className="font-display font-extrabold text-3xl leading-tight mb-3">
            Sécurité renforcée
          </h2>
          <p className="text-af-secondary leading-relaxed max-w-md">
            Pour protéger votre compte AfriMeet contre tout accès frauduleux,
            nous vérifions chaque connexion par un code à usage unique envoyé sur votre email.
          </p>
        </div>
        <div className="text-xs text-af-muted">© 2026 AfriMeet</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-sm text-af-muted hover:text-terracotta mb-8" data-testid="otp-back-link">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Link>
          <h1 className="font-display font-extrabold text-3xl mb-2" data-testid="otp-title">
            {purpose === "signup" ? "Confirmez votre compte" : "Vérifions que c'est bien vous"}
          </h1>
          <p className="text-af-secondary mb-8">
            Nous avons envoyé un code à 6 chiffres à <strong className="text-af">{email}</strong>. Saisissez-le pour continuer.
          </p>
          <form onSubmit={onSubmit}>
            <div className="flex gap-2 sm:gap-3 mb-6" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputsRef.current[i] = el)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={d}
                  onChange={(e) => onChange(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  data-testid={`otp-digit-${i}`}
                  className="w-full h-14 sm:h-16 text-center text-2xl sm:text-3xl font-display font-extrabold border-2 border-af rounded-md focus:outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20 bg-white"
                  disabled={loading}
                />
              ))}
            </div>
            <Button
              type="submit"
              disabled={loading || digits.join("").length !== 6}
              data-testid="otp-submit-btn"
              className="w-full bg-terracotta hover:bg-[#9C7124] text-white h-11 rounded-md font-semibold"
            >
              {loading ? "Vérification…" : "Vérifier le code"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-af-secondary text-center">
            {cooldown > 0 ? (
              <span data-testid="otp-cooldown">Renvoyer le code dans <strong>{cooldown}s</strong></span>
            ) : (
              <button
                type="button"
                onClick={resend}
                data-testid="otp-resend-btn"
                className="text-terracotta font-semibold hover:underline"
              >
                Renvoyer le code
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
